import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import {
  Box,
  Checkbox,
  Grid,
  Stack,
  TableBody,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/system';
import { DataGrid, GridColDef, GridRowSelectionModel, GridValueGetterParams } from '@mui/x-data-grid';

import Accord from '@Components/Accordion';
import SbButton from '@Components/Button';
import DatePicker from '@Components/DatePicker';
import DateRangePicker from '@Components/DateRangePicker';
import SBDropdown from '@Components/Dropdown';
import FilterSelect from '@Components/Filter/Dropdown';
import FilterSearch from '@Components/Filter/Search';
import Image from '@Components/Image';
import { Loader } from '@Components/Loader';
import { SBTable } from '@Components/Table/Table';
import { TableHeadCell, TableRowCell } from '@Components/Table/TableCell';
import { SBTableRow } from '@Components/Table/TableRow';
import SbTextField from '@Components/Textfield';
import Title from '@Components/Title';

import { getCurrencySymbolByName } from '@Constants/countriesWithCurrency';
import { createAccountInvoice } from '@Reducers/Invoice';
import { getInvoiceList } from '@Reducers/InvoiceList';
import { getInvoiceSettings } from '@Reducers/OrganizationSetup';
import { SuccessErrorModalOpen } from '@Reducers/SuccessErrorModal';
import { getSystemList } from '@Reducers/Systems';
import { sendAccountDataTo365 } from '@Reducers/d365';
import { AppDispatch, RootStateType } from '@Store';

import { useAiValidation } from '../../../hooks/useAiValidation';
import { formatNumber, useLocale } from '../../../utils/formatNumber';
import { AccountHolderInvoiceTableProps, AccountHolderUnbilledData, FilterObj } from '../Type';
import CreateInvoiceModal from './components/CreateInvoiceModal';
import {
  FilterType,
  InvConfig,
  SearchFilterList,
  SelectedInvoicesMap,
  SelectedParamType,
  ShipmentTypeList,
  billToOptions,
  buildAccountInvoicePayload,
  calculateSelectedPricingSum,
  getAccountGroupKey,
  getIsWyngsMy,
  initialDateRange,
  initialInvConfig,
  isSameAccountEntry,
  mapShipmentsToGridRows,
  splitTaxOptions,
  updateSelectedAccountRowsList,
  validateInvoiceConfig,
} from './utils/invoiceAccountUtils';

const TableActionContainer = styled('div')(({ theme }) => ({
  margin: '2rem 0',
  display: 'grid',
  gridTemplateColumns: '380px 1fr  fit-content(150px) fit-content(150px) fit-content(150px) 150px',
  columnGap: '15px',
  [theme.breakpoints.down('xl')]: {
    columnGap: '8px',
    gridTemplateColumns: '380px 1fr  fit-content(150px) fit-content(150px) fit-content(150px) 120px',
  },
  [theme.breakpoints.down('lg')]: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
    div: {
      width: 'auto',
    },
  },
}));

const InvoiceTable = React.memo(function InvoiceTable(props: AccountHolderInvoiceTableProps) {
  const { list, selectedInvoices, checkAllInvoices, onSelectInvoice } = props;
  const locale = useLocale();
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};

  const isAllChecked = useMemo(() => {
    if (!list?.length) return false;
    const selectedCount = selectedInvoices ? Object.keys(selectedInvoices).length : 0;
    const nonGeneratableCount = list.filter(item => !item?.canGenerateInvoice).length;
    return selectedCount === nonGeneratableCount;
  }, [list, selectedInvoices]);

  return (
    <SBTable>
      <TableHead>
        <TableRow
          sx={{
            th: {
              textAlign: 'center !important',
            },
          }}
        >
          <TableHeadCell align="center">
            <Checkbox style={{ color: 'inherit' }} checked={isAllChecked} onChange={checkAllInvoices} />
          </TableHeadCell>
          <TableHeadCell align="center">Account No.</TableHeadCell>
          <TableHeadCell align="center">Bill To</TableHeadCell>
          <TableHeadCell align="center">No of Shipments</TableHeadCell>
          <TableHeadCell align="center">System</TableHeadCell>
          <TableHeadCell align="center">Total Amount</TableHeadCell>
          {name !== 'Cargo Solutions Network' && <TableHeadCell align="center">Shipment Type</TableHeadCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {list.map((data, i) => (
          <SBTableRow key={getAccountGroupKey(data) || i} className={selectedInvoices[i] ? 'selected' : ''}>
            <TableRowCell align="center">
              <Checkbox
                style={{ color: 'inherit' }}
                sx={{
                  '&.MuiCheckbox-colorPrimary.Mui-disabled': {
                    color: 'grey !important',
                  },
                }}
                onChange={e => onSelectInvoice(i, e.target.checked)}
                checked={!!selectedInvoices[i]}
              />
            </TableRowCell>
            <TableRowCell
              sx={{
                div: {
                  textAlign: 'center',
                },
              }}
              align="center"
              className="md-text uppercase"
            >
              {data.accountNumber}
            </TableRowCell>
            <TableRowCell
              sx={{
                div: {
                  textAlign: 'start',
                  ml: 2,
                },
              }}
              align="center"
              className="md-text capitalize"
            >
              <p>{data?.company ? data?.company?.name : data?.user ? data?.user?.name : '-'}</p>{' '}
              <p>{data?.company ? data?.company?.email : data?.user ? data?.user?.email : '-'}</p>
            </TableRowCell>
            <TableRowCell
              sx={{
                div: {
                  textAlign: 'center',
                },
              }}
              align="center"
              className="md-text"
            >
              {data.shipments?.length}
            </TableRowCell>
            <TableRowCell
              sx={{
                div: {
                  textAlign: 'center',
                },
              }}
              align="center"
              className="md-text"
            >
              {data._systemId?.name}
            </TableRowCell>
            <TableRowCell
              sx={{
                div: {
                  textAlign: 'center',
                },
              }}
              align="center"
              className="md-text"
            >
              <span title={data.shipments[0]?.sellingPricing?.currency}>
                {getCurrencySymbolByName(data.shipments[0]?.sellingPricing?.currency) ||
                  getCurrencySymbolByName(data.shipments[0]?._packageId?.currency) ||
                  null}{' '}
                {formatNumber(parseFloat(data.totalAmount || '0'), locale)}
              </span>
            </TableRowCell>
            {name !== 'Cargo Solutions Network' && (
              <TableRowCell
                sx={{
                  div: {
                    textAlign: 'center',
                  },
                }}
                className="md-text"
                align="center"
              >
                {data.shipmentRouteType}
              </TableRowCell>
            )}
          </SBTableRow>
        ))}
      </TableBody>
    </SBTable>
  );
});

export default function InvoiceList() {
  const [filterSelectValue, setFilterSelectValue] = useState('account_no');
  const [filterText, setFilterText] = useState('');
  const [step, setStep] = useState(1);
  const [isClear, setClear] = useState(false);
  const [system, setSystem] = useState<string[]>([]);
  const [shipmentType, setShipmentType] = useState<string[]>([]);
  const [invConfig, setInvConfig] = useState<InvConfig>(initialInvConfig);
  const [dateRange, setDateRange] = useState(initialDateRange);
  const [selectedInvoices, setSelectedInvoices] = useState<SelectedInvoicesMap>({});
  const [selectedUnbilledRow, setSelectedUnbilledRow] = useState<AccountHolderUnbilledData[]>([]);
  const [selectedAccountRows, setSelectedAccountRows] = useState<SelectedParamType[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isWyngsMy = useMemo(() => getIsWyngsMy(), []);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};

  const selectedSystemList = useSelector((state: RootStateType) => state.System.data.selectedList);
  const _systems = useSelector((state: RootStateType) => state.loginUser?.profile?._systems);

  const activeSystemList = useMemo(() => {
    if (!_systems?.length || !selectedSystemList?.length) return [];
    return selectedSystemList
      .filter(sys => _systems.includes(sys._id))
      .map(sys => ({
        display: sys.name,
        value: sys._id,
      }));
  }, [selectedSystemList, _systems]);

  const {
    unBilledList: { list, currentPage = 1, perPage, totalCount },
    loading,
  } = useSelector((state: RootStateType) => state.InvoiceList);

  const { invoiceSetting } = useSelector((state: RootStateType) => state?.orgSetup);
  const taxInformationOptions = useMemo(() => {
    const taxInformation = invoiceSetting?.taxInformation || [];
    return taxInformation.map(s => ({ display: s.display, value: s.value, key: s.key }));
  }, [invoiceSetting?.taxInformation]);

  useEffect(() => {
    const fromDate = moment(dateRange.startDate).format('DD/MM/YYYY');
    const toDate = moment(dateRange.endDate).format('DD/MM/YYYY');
    dispatch(getInvoiceList({ currentPage: 1, perPage: 10, fromDate, toDate }));
    dispatch(getSystemList());
  }, [dispatch]);

  const getFilterObj = useCallback(
    (options: FilterObj) => {
      setSelectedInvoices({});
      const filterDate = options.dateRange || dateRange;
      const fromDate = moment(filterDate.startDate).format('DD/MM/YYYY');
      const toDate = moment(filterDate.endDate).format('DD/MM/YYYY');
      const filterSystem = options.system || system;
      const filterType = options.shipmentType || shipmentType;
      const filterSearchKey = filterSelectValue;
      const filterSearchValue = 'searchValue' in options ? options.searchValue : filterText;
      const filter: FilterType = {
        currentPage: options.currentPage || currentPage,
        perPage: options.perPage || perPage,
        fromDate,
        toDate,
        searchKey: filterSearchKey,
        searchValue: filterSearchValue,
        systems: filterSystem,
        type: filterType,
      };

      if (!filterSearchValue) {
        delete filter.searchKey;
        delete filter.searchValue;
      }

      return filter;
    },
    [dateRange, system, shipmentType, filterSelectValue, filterText, currentPage, perPage],
  );

  useEffect(() => {
    if (isClear) {
      dispatch(getInvoiceList(getFilterObj({})));
      setClear(false);
    }
  }, [isClear, dispatch, getFilterObj]);

  const onDateChange = useCallback(
    ({ startDate, endDate }: { startDate: Date; endDate: Date }, isOpen: boolean) => {
      if (isOpen) return;
      const nextDateRange = { startDate, endDate };
      setDateRange(nextDateRange);
      dispatch(getInvoiceList(getFilterObj({ dateRange: nextDateRange })));
    },
    [dispatch, getFilterObj],
  );

  const onSystemFilter = useCallback(
    (selected: Array<string>) => {
      setSystem(selected);
      dispatch(getInvoiceList(getFilterObj({ system: selected })));
    },
    [dispatch, getFilterObj],
  );

  const onTypeFilter = useCallback(
    (selected: Array<string>) => {
      setShipmentType(selected);
      dispatch(getInvoiceList(getFilterObj({ shipmentType: selected })));
    },
    [dispatch, getFilterObj],
  );

  const onClear = useCallback(() => {
    setSystem([]);
    setShipmentType([]);
    setFilterText('');
    setDateRange(initialDateRange);
    setClear(true);
  }, []);

  const onSearchSelectChange = useCallback((value: string) => {
    setFilterSelectValue(value);
  }, []);

  const onSearchTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setFilterText(e.target.value);
  }, []);

  const onTextSearch = useCallback(() => {
    dispatch(getInvoiceList(getFilterObj({})));
  }, [dispatch, getFilterObj]);

  const onTextClear = useCallback(() => {
    setFilterText('');
    dispatch(getInvoiceList(getFilterObj({ searchValue: undefined })));
  }, [dispatch, getFilterObj]);

  const onPageChange = useCallback(
    (_: unknown, page: number) => {
      dispatch(getInvoiceList(getFilterObj({ currentPage: page + 1 })));
    },
    [dispatch, getFilterObj],
  );

  const onRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      dispatch(getInvoiceList(getFilterObj({ perPage: parseInt(event.target.value, 10) })));
    },
    [dispatch, getFilterObj],
  );

  const onSelectInvoice = useCallback(
    (invoiceId: number, isChecked: boolean) => {
      setSelectedInvoices(prev => {
        const next = { ...prev };
        if (isChecked) next[invoiceId] = true;
        else delete next[invoiceId];
        return next;
      });
      const markedRow = list[invoiceId];
      if (!markedRow) return;
      setSelectedUnbilledRow(prev => {
        if (isChecked) {
          return [...prev, { ...markedRow, shipments: [] }];
        }
        return prev.filter(
          item =>
            item.accountNumber !== markedRow.accountNumber ||
            item.shipmentRouteType !== markedRow.shipmentRouteType ||
            item._systemId?._id !== markedRow._systemId?._id,
        );
      });
    },
    [list],
  );

  const checkAllInvoices = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        const newInvoiceCheckedList = list.reduce((op: { [k: string]: boolean }, item, index) => {
          if (item?.canGenerateInvoice) {
            op[index] = true;
          }
          return op;
        }, {});
        setSelectedInvoices(newInvoiceCheckedList);
      } else {
        setSelectedInvoices({});
        setSelectedUnbilledRow([]);
      }
    },
    [list],
  );

  const setSelectedUnbilledShipment = useCallback((_params: SelectedParamType) => {
    setSelectedAccountRows(prev => updateSelectedAccountRowsList(prev, _params));
  }, []);

  const submitInvoiceCreation = useCallback(
    (config: InvConfig, accountRows: SelectedParamType[], onSuccess?: () => void) => {
      if (isSubmitting) return;

      const missingFields = validateInvoiceConfig(config, accountRows, isWyngsMy);

      if (missingFields.length > 0) {
        dispatch(
          SuccessErrorModalOpen({
            type: 'error',
            title: 'Validation Error',
            subTitle: `Please complete all required fields before proceeding. ${missingFields.join(', ')}`,
          }),
        );
        return;
      }

      const payload = buildAccountInvoicePayload(config, accountRows, taxInformationOptions);

      setIsSubmitting(true);
      dispatch(createAccountInvoice(payload))
        .then(res => {
          if (res.type === 'createAccountInvoice/fulfilled') {
            if (onSuccess) onSuccess();
            navigate('/invoice/view');
          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
    [isSubmitting, isWyngsMy, dispatch, taxInformationOptions, navigate],
  );

  const createUnbilledInvoice = useCallback(() => {
    submitInvoiceCreation(invConfig, selectedAccountRows);
  }, [submitInvoiceCreation, invConfig, selectedAccountRows]);

  const nextStep = useCallback(() => {
    setStep(prev => (prev === 3 ? prev : prev + 1));
  }, []);

  const preStep = useCallback(() => {
    setStep(prev => {
      if (prev <= 0) return prev;
      if (prev === 2) {
        setSelectedAccountRows([]);
      }
      return prev - 1;
    });
  }, []);

  const stepThreeMissingFields = useMemo(
    () => (step === 3 ? validateInvoiceConfig(invConfig, selectedAccountRows, isWyngsMy) : []),
    [step, invConfig, selectedAccountRows, isWyngsMy],
  );

  const isPrevDisabled = step <= 1;
  const isNextDisabled =
    isSubmitting ||
    (step === 1 && Object.keys(selectedInvoices).length === 0) ||
    (step === 2 && selectedAccountRows.length === 0) ||
    (step === 3 && stepThreeMissingFields.length > 0);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    if (isSubmitting) return;
    setIsModalOpen(false);
  }, [isSubmitting]);

  const handleRestorePageFilters = useCallback(() => {
    const fromDate = moment(dateRange.startDate).format('DD/MM/YYYY');
    const toDate = moment(dateRange.endDate).format('DD/MM/YYYY');
    const filter: FilterType = {
      currentPage,
      perPage,
      fromDate,
      toDate,
      searchKey: filterSelectValue,
      searchValue: filterText,
      systems: system,
      type: shipmentType,
    };
    if (!filterText) {
      delete filter.searchKey;
      delete filter.searchValue;
    }
    dispatch(getInvoiceList(filter));
  }, [dateRange, currentPage, perPage, filterSelectValue, filterText, system, shipmentType, dispatch]);

  return (
    <>
      <Loader loading={loading || isSubmitting} />
      <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <Box
          onClick={() => {
            if (step === 1) {
              navigate('/invoice/view');
            } else if (step === 2) {
              setStep(1);
            } else {
              setStep(2);
            }
          }}
          sx={{ zIndex: '1', cursor: 'pointer' }}
        >
          <Image src="/images/backbutton.svg" alt="Back Button" style={{ width: '1.5rem', marginTop: '0.2rem' }} />
        </Box>
        <Title title="Invoices" subTitle="View and create invoices" />
      </Box>
      <Box
        className="flex"
        sx={{
          marginTop: '52px',
          marginBottom: '25px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        <Box>
          <Title title="Create Invoices" removeBorder removePadding titleClass="underline px_20-text" />
        </Box>
        <SbButton
          variant="contained"
          className="rounded-full"
          startIcon={<ReceiptLongOutlinedIcon fontSize="small" />}
          sx={{
            borderRadius: '10px !important',
            padding: '8px 20px !important',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
          onClick={handleOpenModal}
        >
          Quick Invoice
        </SbButton>
      </Box>
      {step === 1 && (
        <>
          <TableActionContainer>
            <FilterSearch
              onSelectChange={onSearchSelectChange}
              list={SearchFilterList}
              selectValue={filterSelectValue}
              textValue={filterText}
              onTextChange={onSearchTextChange}
              onTextSearch={onTextSearch}
              onTextClear={onTextClear}
            />
            <DateRangePicker onChange={onDateChange} value={dateRange} />
            <div></div>
            <FilterSelect
              id="system"
              label="System"
              value={system}
              options={activeSystemList}
              onChange={onSystemFilter}
            />
            {name === 'Cargo Solutions Network' ? (
              <div></div>
            ) : (
              <FilterSelect
                id="shipmenttype"
                label="Shipment Type"
                value={shipmentType}
                options={ShipmentTypeList}
                onChange={onTypeFilter}
              />
            )}
            <SbButton
              className="xs-text ml-1"
              variant="outlined"
              sx={{ borderRadius: '8px !important', padding: '8px !important' }}
              onClick={onClear}
            >
              Clear Filters
            </SbButton>
          </TableActionContainer>
          <Box my={'2rem'} pb={'0.5rem'} borderBottom={'4px solid #080808'} width={'220px'}>
            <Typography className="md-text" fontWeight={700}>
              Select Unbilled Shipments
            </Typography>
          </Box>
          <div
            style={{
              display: 'block',
              width: '100%',
              overflowX: 'auto',
              overflowY: 'hidden',
              maxWidth: '100%',
            }}
          >
            <div
              style={{
                minWidth: '600px',
              }}
            >
              <InvoiceTable
                checkAllInvoices={checkAllInvoices}
                onSelectInvoice={onSelectInvoice}
                selectedInvoices={selectedInvoices}
                list={list}
              />
            </div>
            {totalCount ? (
              <TablePagination
                component="div"
                count={totalCount}
                page={currentPage - 1}
                rowsPerPage={perPage}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
              />
            ) : (
              ''
            )}
          </div>
        </>
      )}
      {step === 2 && (
        <StepTwo
          selectedInvoices={selectedInvoices}
          setSelectedUnbilledShipment={setSelectedUnbilledShipment}
          list={list}
          selectedUnbilledRow={selectedAccountRows}
        />
      )}
      {step === 3 && (
        <StepThree
          selectedAccountRows={selectedAccountRows}
          selectedUnbilledRow={selectedUnbilledRow}
          list={list}
          invConfig={invConfig}
          setInvConfig={setInvConfig}
          selectedInvoices={selectedInvoices}
        />
      )}

      <Grid container item mt={2} justifyContent="flex-end" spacing={4}>
        <Grid item xs={12} sm={3} md={2}>
          <SbButton variant="outlined" fullWidth className="rounded-full" onClick={preStep} disabled={isPrevDisabled}>
            Back
          </SbButton>
        </Grid>
        <Grid item xs={12} sm={3} md={2}>
          <Tooltip
            title={
              step === 3 && stepThreeMissingFields.length > 0 ? (
                <Box sx={{ p: 0.5, maxWidth: 300 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, mb: 0.5, color: 'inherit' }}>
                    Cannot create invoice. Required field(s) missing:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2, fontSize: '0.75rem', lineHeight: 1.5 }}>
                    {stepThreeMissingFields.map(field => (
                      <li key={field}>{field}</li>
                    ))}
                  </Box>
                </Box>
              ) : (
                ''
              )
            }
            arrow
            enterDelay={150}
            placement="top"
            disableHoverListener={!(step === 3 && stepThreeMissingFields.length > 0)}
          >
            <span style={{ display: 'block', width: '100%' }}>
              <SbButton
                variant="contained"
                fullWidth
                className="rounded-full"
                onClick={step === 3 ? createUnbilledInvoice : nextStep}
                disabled={isNextDisabled}
                loading={step === 3 && isSubmitting}
              >
                {step === 3 ? 'Create' : 'Next'}
              </SbButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>

      <CreateInvoiceModal
        open={isModalOpen}
        onClose={handleCloseModal}
        activeSystemList={activeSystemList}
        isSubmitting={isSubmitting}
        onSubmitInvoice={submitInvoiceCreation}
        initialFilterSelectValue={filterSelectValue}
        initialFilterText={filterText}
        initialDateRange={dateRange}
        initialSystem={system}
        initialShipmentType={shipmentType}
        onRestorePageFilters={handleRestorePageFilters}
      />
    </>
  );
}

type StepTwoProps = {
  selectedInvoices: SelectedInvoicesMap;
  setSelectedUnbilledShipment: (obj: SelectedParamType) => void;
  list: AccountHolderUnbilledData[];
  selectedUnbilledRow: SelectedParamType[];
};

const StepTwo = React.memo(function StepTwo(props: StepTwoProps) {
  const { selectedInvoices, list, setSelectedUnbilledShipment, selectedUnbilledRow } = props;
  const selectedShipmentsData = useMemo(
    () =>
      Object.keys(selectedInvoices)
        .map((i: string) => list[Number(i)])
        .filter(Boolean),
    [selectedInvoices, list],
  );

  return (
    <>
      <Box my={'2rem'} pb={'0.5rem'} borderBottom={'4px solid #080808'} width={'220px'}>
        <Typography className="md-text" fontWeight={700}>
          Select Unbilled Shipments
        </Typography>
      </Box>
      {selectedShipmentsData.map((s, idx) => (
        <AccordionTable
          key={getAccountGroupKey(s) || idx}
          data={s}
          setSelectedUnbilledShipment={setSelectedUnbilledShipment}
          selectedUnbilledRow={selectedUnbilledRow?.filter(item =>
            s?.uniqueKey ? item?.uniqueKey === s?.uniqueKey : isSameAccountEntry(item, s),
          )}
        />
      ))}
    </>
  );
});

type StepThreeProps = {
  selectedAccountRows: SelectedParamType[];
  selectedUnbilledRow: AccountHolderUnbilledData[];
  selectedInvoices: SelectedInvoicesMap;
  list: AccountHolderUnbilledData[];
  invConfig: InvConfig;
  setInvConfig: React.Dispatch<React.SetStateAction<InvConfig>>;
};

const StepThree = React.memo(function StepThree(props: StepThreeProps) {
  const { selectedAccountRows, invConfig, setInvConfig } = props;
  const { invoiceSetting, loading } = useSelector((state: RootStateType) => state?.orgSetup);
  const { loading: loadingD365 } = useSelector((state: RootStateType) => state?.D365);
  const dispatch = useDispatch<AppDispatch>();
  const isWyngsMy = useMemo(() => getIsWyngsMy(), []);

  const { validationStates, validate } = useAiValidation();

  useEffect(() => {
    if (invConfig.taxname && invConfig.taxno) {
      const lowerName = invConfig.taxname.toLowerCase();
      if (lowerName.includes('gst')) {
        validate('taxno', 'gst', invConfig.taxno);
      } else if (lowerName.includes('pan')) {
        validate('taxno', 'pan', invConfig.taxno);
      }
    }
  }, [invConfig.taxname, invConfig.taxno, validate]);

  const handleApplyTaxnoSuggestion = useCallback(
    (suggestion: string) => {
      setInvConfig(prev => ({ ...prev, taxno: suggestion }));
    },
    [setInvConfig],
  );

  const sacCodeOptions = useMemo(() => {
    const sacCode = invoiceSetting?.sacCode || [];
    return sacCode.map(s => ({ display: s.display, value: s.key }));
  }, [invoiceSetting?.sacCode]);

  const taxInformationOptions = useMemo(() => {
    const taxInformation = invoiceSetting?.taxInformation || [];
    return taxInformation.map(s => ({ display: s.display, value: s.key }));
  }, [invoiceSetting?.taxInformation]);

  const noOfInvoices = selectedAccountRows?.length || 0;
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};

  useEffect(() => {
    if (!invoiceSetting) {
      dispatch(getInvoiceSettings());
    }
  }, [dispatch, invoiceSetting]);

  const sendTo365 = useCallback(() => {
    const accountData = selectedAccountRows.map((item: SelectedParamType) => ({
      _userId: item?._userId,
      accountNo: item.accountNumber,
      _systemId: item._systemId,
      _shipmentIds: item.shipments.map(s => s._id),
      _shipmentNumbers: item.shipments.map(s => s.shipmentNumber),
    }));
    dispatch(
      sendAccountDataTo365({
        accountData,
        dueDate: dayjs(invConfig.dueDate).format('YYYY-MM-DD'),
        invoiceDate: dayjs(invConfig.invoiceDate).format('YYYY-MM-DD'),
      }),
    );
  }, [selectedAccountRows, dispatch, invConfig.dueDate, invConfig.invoiceDate]);

  return (
    <>
      <Loader loading={loading || loadingD365} />

      <Grid container>
        <Grid item md={5} container gap={2} mt={'3.8rem'}>
          <Grid item md={9} container columnSpacing={4}>
            <Grid item md={6}>
              <DatePicker
                label="Invoice Date"
                value={invConfig.invoiceDate}
                handleChange={(date: Dayjs | null) =>
                  setInvConfig(prev => ({
                    ...prev,
                    invoiceDate: date,
                    dueDate: date ? date.add(1, 'month') : null,
                  }))
                }
                required
              />
            </Grid>
            <Grid item md={6}>
              <DatePicker
                label="Due Date"
                value={invConfig.dueDate}
                handleChange={(date: Dayjs | null) => setInvConfig(prev => ({ ...prev, dueDate: date }))}
                minDate={invConfig.invoiceDate ? invConfig.invoiceDate.add(1, 'day') : undefined}
                disabled
                required
              />
            </Grid>
          </Grid>
          {name !== 'Cargo Solutions Network' && (
            <>
              <Grid item md={9}>
                <SBDropdown
                  label="Select Taxation"
                  id="taxBreakdown"
                  placeholder="Select"
                  options={taxInformationOptions}
                  onChange={value => {
                    setInvConfig(prev => ({ ...prev, taxBreakdown: value }));
                  }}
                  name="taxBreakdown"
                  value={invConfig.taxBreakdown || []}
                />
              </Grid>
              <Grid item md={9}>
                <SBDropdown
                  label="SAC Code"
                  id="sacCode"
                  placeholder="Select"
                  options={sacCodeOptions}
                  onChange={value => {
                    setInvConfig(prev => ({ ...prev, sacCode: value }));
                  }}
                  name="sacCode"
                  value={invConfig.sacCode || []}
                />
              </Grid>
              <Grid item md={9}>
                <SBDropdown
                  label="Tax Breakdown"
                  id="splitTax"
                  placeholder="Select"
                  options={splitTaxOptions}
                  onChange={value => {
                    setInvConfig(prev => ({ ...prev, splitTax: value }));
                  }}
                  name="splitTax"
                  value={invConfig.splitTax || []}
                />
              </Grid>
              <Grid item md={9}>
                <SBDropdown
                  label="Billing To"
                  id="billTo"
                  placeholder="Select"
                  options={billToOptions}
                  onChange={value => {
                    setInvConfig(prev => ({ ...prev, billTo: value }));
                  }}
                  name="billTo"
                  value={invConfig.billTo || []}
                />
              </Grid>
            </>
          )}
          {/* Third party */}
          {invConfig?.billTo && invConfig?.billTo[0] === 'third_party' && (
            <>
              <Grid item md={9}>
                <SbTextField
                  placeholder="Bill Name"
                  label="Bill Name"
                  name="billname"
                  value={invConfig.billname}
                  onChange={e => {
                    const { value } = e.target;
                    setInvConfig(prev => ({ ...prev, billname: value }));
                  }}
                />
              </Grid>
              <Grid item md={9}>
                <SbTextField
                  placeholder="Bill Address"
                  label="Bill Address"
                  name="billaddres"
                  value={invConfig.billaddress}
                  onChange={e => {
                    const { value } = e.target;
                    setInvConfig(prev => ({ ...prev, billaddress: value }));
                  }}
                />
              </Grid>
              <Grid item md={9}>
                <Grid container columnSpacing={4}>
                  <Grid item md={6} gap={4}>
                    <SbTextField
                      placeholder="Tax Name"
                      label="Tax Name"
                      name="taxname"
                      value={invConfig.taxname}
                      onChange={e => {
                        const { value } = e.target;
                        setInvConfig(prev => ({ ...prev, taxname: value }));
                      }}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <SbTextField
                      placeholder="Tax No"
                      label="Tax No"
                      name="taxno"
                      value={invConfig.taxno}
                      onChange={e => {
                        const { value } = e.target;
                        setInvConfig(prev => ({ ...prev, taxno: value }));
                      }}
                      validationState={validationStates['taxno']}
                      onApplySuggestion={handleApplyTaxnoSuggestion}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
          <Grid item md={9}>
            <div style={{ display: 'flex', marginTop: '10px' }}>
              {!isWyngsMy && name !== 'Cargo Solutions Network' && (
                <SbButton
                  className="rounded-full"
                  variant="contained"
                  sx={{ borderRadius: '8px !important', padding: '8px !important' }}
                  onClick={sendTo365}
                  disabled={!(invConfig.invoiceDate && invConfig.dueDate)}
                >
                  Send to D365
                </SbButton>
              )}
            </div>
          </Grid>
        </Grid>
        <Grid item xs={12} md={6.8}>
          <Grid container justifyContent={'space-between'} alignItems={'center'} my={'1rem'}>
            <Grid item xs="auto">
              <Box pb={'0.5rem'} borderBottom={'4px solid #080808'} width={'140px'}>
                <Title removePadding removeBorder title="Preview Invoices" titleClass=" px_16-text" />
              </Box>
            </Grid>
            <Grid item xs="auto" justifyContent={'flex-end'} container>
              <Title removePadding removeBorder title={`No. of Invoices : ${noOfInvoices}`} titleClass="px_16-text" />
            </Grid>
          </Grid>
          <Box sx={{ overflowX: 'auto' }}>
            <SBTable>
              <TableHead>
                <TableRow>
                  <TableHeadCell>Sr. No.</TableHeadCell>
                  <TableHeadCell>Account No.</TableHeadCell>
                  {name !== 'Cargo Solutions Network' && <TableHeadCell>System</TableHeadCell>}
                  <TableHeadCell>No of Shipments</TableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedAccountRows.map((data, index) => (
                  <SBTableRow key={getAccountGroupKey(data) || index}>
                    <TableRowCell
                      sx={{
                        div: {
                          textAlign: 'center',
                        },
                      }}
                      align="center"
                    >
                      {index + 1}
                    </TableRowCell>
                    <TableRowCell align="center" className="md-text uppercase">
                      {data.accountNumber}
                    </TableRowCell>
                    {name !== 'Cargo Solutions Network' && (
                      <TableRowCell align="center" className="md-text capitalize">
                        <p>{data.systemName}</p>
                      </TableRowCell>
                    )}

                    <TableRowCell
                      sx={{
                        div: {
                          textAlign: 'center',
                        },
                      }}
                      align="center"
                      className="md-text"
                    >
                      {data.shipments.length}
                    </TableRowCell>
                  </SBTableRow>
                ))}
              </TableBody>
            </SBTable>
          </Box>
        </Grid>
      </Grid>
    </>
  );
});

type AccordionTableProps = {
  data: AccountHolderUnbilledData;
  setSelectedUnbilledShipment: (obj: SelectedParamType) => void;
  selectedUnbilledRow: SelectedParamType[];
};

const AccordionTable = React.memo(function AccordionTable(props: AccordionTableProps) {
  const { data, setSelectedUnbilledShipment, selectedUnbilledRow } = props;
  const locale = useLocale();

  const { shipments, shipmentRouteType, accountNumber, count, _systemId, accountId, user, company, uniqueKey } = data;

  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};

  const rows = useMemo(() => mapShipmentsToGridRows(shipments, name, locale), [shipments, name, locale]);

  const totalPricingSum = useMemo(() => calculateSelectedPricingSum(selectedUnbilledRow), [selectedUnbilledRow]);

  const currencySymbol = useMemo(
    () =>
      getCurrencySymbolByName(shipments?.[0]?.sellingPricing?.currency) ||
      getCurrencySymbolByName(shipments?.[0]?._packageId?.currency) ||
      '',
    [shipments],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'date', headerName: 'Date', width: 130, cellClassName: 'md-text', headerClassName: 'md-text' },
      {
        field: 'shipmentNo',
        headerName: 'Shipment No',
        width: 150,
        renderCell: (params: GridValueGetterParams) => (
          <Link to={`/shipment/admin/edit/${params?.value}`} className="sb-text-black underline">
            {params?.value}
          </Link>
        ),
        cellClassName: 'md-text',
        headerClassName: 'md-text',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 160,
        cellClassName: 'md-text',
        headerClassName: 'md-text',
      },
      {
        field: 'shipper',
        headerName: 'Shipper',
        description: 'This column has a value getter and is not sortable.',
        sortable: false,
        width: 180,
        renderCell: (params: GridValueGetterParams) => (
          <Box>
            <p className="font-medium capitalize">{params?.value?.name}</p>
            <p className="sb-text-gray">
              {params?.value?.city}-{params?.value?.pincode}
            </p>
            <p className="sb-text-gray">{params?.value?.country}</p>
          </Box>
        ),
        cellClassName: 'md-text h-min',
        headerClassName: 'md-text',
      },
      {
        field: 'consignee',
        headerName: 'Consignee',
        description: 'This column has a value getter and is not sortable.',
        sortable: false,
        width: 180,
        renderCell: (params: GridValueGetterParams) => (
          <Box>
            <p className="font-medium capitalize">{params?.value?.name}</p>
            <p className="sb-text-gray">
              {params?.value?.city}-{params?.value?.pincode}
            </p>
            <p className="sb-text-gray">{params?.value?.country}</p>
          </Box>
        ),
        cellClassName: 'md-text',
        headerClassName: 'md-text',
      },
      {
        field: 'weight',
        headerName: 'Weight',
        width: 160,
        cellClassName: 'md-text',
        headerClassName: 'md-text',
      },
      {
        field: 'packages',
        headerName: 'Packages',
        width: 160,
        cellClassName: 'md-text',
        headerClassName: 'md-text',
      },
      {
        field: 'totalAmount',
        headerName: 'Total Amount',
        width: 180,
        cellClassName: 'md-text',
        headerClassName: 'md-text',
        renderHeader: () => (
          <Tooltip title={`${shipments?.[0]?.sellingPricing?.currency || ''}`} arrow>
            <span>{`Total Amount (${currencySymbol})`}</span>
          </Tooltip>
        ),
      },
    ],
    [shipments, currencySymbol],
  );

  const rowSelectionModel = useMemo(() => {
    if (!selectedUnbilledRow?.length || !shipments?.length) return [];
    const indexById = new Map<string, number>();
    shipments.forEach((s, idx) => {
      if (s?._id) indexById.set(s._id, idx + 1);
    });
    return selectedUnbilledRow.flatMap((item: SelectedParamType) =>
      (item?.shipments || []).map(ship => indexById.get(ship?._id) ?? -1).filter(id => id !== -1),
    );
  }, [selectedUnbilledRow, shipments]);

  const handleRowSelectionChange = useCallback(
    (selectedRows: GridRowSelectionModel) => {
      const selectedSet = new Set(selectedRows.map(Number));
      const shipmentRow = shipments.filter((_, i) => selectedSet.has(i + 1));
      const obj: SelectedParamType = {
        _userId: user?._id,
        accountNumber,
        shipmentRouteType: shipmentRouteType || '',
        _systemId: _systemId?._id || '',
        systemName: _systemId?.name || '',
        shipments: shipmentRow,
        uniqueKey,
      };
      setSelectedUnbilledShipment(obj);
    },
    [shipments, user?._id, accountNumber, shipmentRouteType, _systemId, uniqueKey, setSelectedUnbilledShipment],
  );

  const summaryNode = (
    <Stack
      direction={'row'}
      justifyContent={'space-between'}
      pr={'1rem'}
      sx={{
        '@media (max-width: 600px)': {
          flexDirection: 'column',
          alignItems: 'flex-start',
        },
      }}
    >
      <Stack gap={'10px'} sx={{ width: '100%' }}>
        <Stack direction={'row'} gap={'2rem'} sx={{ '@media (max-width: 600px)': { flexDirection: 'column' } }}>
          <Box
            sx={{
              minWidth: '5rem',
              fontSize: '1rem',
              '@media (max-width: 600px)': {
                fontSize: '0.875rem',
                width: '100%',
              },
            }}
            className="md-text"
          >
            {company ? company?.name : user ? user?.name : '-'}
          </Box>
          <Typography
            className="md-text has-text-grey-light"
            sx={{
              fontSize: '0.875rem',
              '@media (max-width: 600px)': {
                fontSize: '0.75rem',
              },
            }}
          >
            {company ? company?.email : user ? user?.email : '-'}
          </Typography>
        </Stack>

        <Stack direction={'row'} gap={'2rem'} sx={{ '@media (max-width: 600px)': { flexDirection: 'column' } }}>
          <Typography
            className="md-text font-semibold"
            sx={{
              fontSize: '0.875rem',
              '@media (max-width: 600px)': { fontSize: '0.75rem' },
            }}
          >
            {accountId?.accountNumber}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.875rem',
              '@media (max-width: 600px)': { fontSize: '0.75rem' },
            }}
          >
            {_systemId?.name}
          </Typography>
          {name !== 'Cargo Solutions Network' && (
            <Typography
              className="md-text capitalize"
              sx={{
                fontSize: '0.875rem',
                '@media (max-width: 600px)': { fontSize: '0.75rem' },
              }}
            >
              {shipmentRouteType}
            </Typography>
          )}
          <Typography
            className="md-text"
            sx={{
              fontSize: '0.875rem',
              '@media (max-width: 600px)': { fontSize: '0.75rem' },
            }}
          >
            Shipments({count})
          </Typography>
        </Stack>
      </Stack>
      <Box
        sx={{
          justifySelf: 'flex-end',
          alignSelf: 'center',
          fontWeight: 600,
          color: 'primary.main',
          fontSize: '1rem',
          '@media (max-width: 600px)': {
            fontSize: '0.875rem',
            width: '100%',
            textAlign: 'left',
          },
        }}
        className="md-text"
      >
        <span title={shipments[0]?.sellingPricing?.currency}>
          Total Amount: {currencySymbol || null} {formatNumber(totalPricingSum, locale)}
        </span>
      </Box>
    </Stack>
  );

  const detailsNode = (
    <DataGrid
      rows={rows}
      columns={columns}
      initialState={{
        pagination: {
          paginationModel: { page: 0, pageSize: 10 },
        },
      }}
      rowHeight={90}
      rowSelectionModel={rowSelectionModel}
      checkboxSelection
      onRowSelectionModelChange={handleRowSelectionChange}
    />
  );

  return <Accord summary={summaryNode} details={detailsNode} />;
});
